import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';

import { StyledDialog } from 'theme/index';
import {
  downloadUploadedDocumentAPI,
  getGuardianUploadedDocumentsAPI,
  getStaffUploadedDocumentsAPI,
  isStaffAcceptedUploadFile,
  PatientUploadedDocument,
  STAFF_UPLOAD_ACCEPT_ATTR,
  uploadStaffDocumentAPI,
} from 'services/documentServices';
import { formatDateTime } from 'utils/dataFormatter';

import DocumentActionIcons from './Documents/components/DocumentActionIcons';
import DocumentPreviewDialog from './Documents/components/DocumentPreviewDialog';
import ImagePreviewDialog from './Documents/components/ImagePreviewDialog';
import {
  downloadRawImagesZipAPI,
  fetchGeneratedPdfBlobUrlAPI,
  fetchRawImagesAPI,
  softDeleteDocumentAPI,
} from './Documents/services/documentReviewService';

interface StaffUploadDocumentsProps {
  patientId: string | number;
}

const StaffUploadDocuments: React.FC<StaffUploadDocumentsProps> = ({ patientId }) => {
  const patientIdStr = patientId ? String(patientId) : '';

  const inputRef = useRef<HTMLInputElement>(null);
  const pdfBlobUrlRef = useRef<string | undefined>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const [staffDocumentsOpen, setStaffDocumentsOpen] = useState(false);
  const [guardianDocumentsOpen, setGuardianDocumentsOpen] = useState(false);
  const [staffDocuments, setStaffDocuments] = useState<PatientUploadedDocument[]>([]);
  const [guardianDocuments, setGuardianDocuments] = useState<PatientUploadedDocument[]>([]);
  const [staffDocumentsLoading, setStaffDocumentsLoading] = useState(false);
  const [guardianDocumentsLoading, setGuardianDocumentsLoading] = useState(false);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<number | null>(null);
  const [downloadingImagesDocumentId, setDownloadingImagesDocumentId] = useState<number | null>(null);

  const [activeDocument, setActiveDocument] = useState<PatientUploadedDocument | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | undefined>();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<PatientUploadedDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const revokePdfBlobUrl = useCallback(() => {
    if (pdfBlobUrlRef.current) {
      URL.revokeObjectURL(pdfBlobUrlRef.current);
      pdfBlobUrlRef.current = undefined;
    }
    setPdfBlobUrl(undefined);
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!isStaffAcceptedUploadFile(file)) {
      setFileError('Invalid file type. Allowed: JPG, JPEG, PNG, WEBP, PDF, DOC, DOCX.');
      setSelectedFile(null);
      return;
    }

    setFileError(null);
    setSelectedFile(file);
    setDocumentName((current) => current || file.name.replace(/\.[^.]+$/, ''));
  }, []);

  const resetUploadForm = useCallback(() => {
    setSelectedFile(null);
    setDocumentName('');
    setDocumentDescription('');
    setFileError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedFile) {
      toast('Please select a file to upload.', { type: 'info' });
      return;
    }

    if (!documentName.trim()) {
      toast('Document name is required.', { type: 'error' });
      return;
    }

    if (!patientIdStr) {
      toast('Patient information is missing.', { type: 'error' });
      return;
    }

    setIsUploading(true);
    try {
      await uploadStaffDocumentAPI({
        patientId: patientIdStr,
        documentName: documentName.trim(),
        documentDescription: documentDescription.trim() || undefined,
        sourceFile: selectedFile,
      });
      toast('Document uploaded successfully.', { type: 'success' });
      resetUploadForm();
    } catch (error) {
      console.error('Staff document upload failed:', error);
      toast(error instanceof Error ? error.message : 'Document upload failed. Please try again.', { type: 'error' });
    } finally {
      setIsUploading(false);
    }
  }, [documentDescription, documentName, patientIdStr, resetUploadForm, selectedFile]);

  const loadStaffDocuments = useCallback(() => {
    setStaffDocumentsLoading(true);
    getStaffUploadedDocumentsAPI(Number(patientIdStr))
      .then((documents) => {
        setStaffDocuments(documents ?? []);
      })
      .catch(() => {
        setStaffDocuments([]);
        toast('Unable to load uploaded documents. Please try again.', { type: 'error' });
      })
      .finally(() => {
        setStaffDocumentsLoading(false);
      });
  }, [patientIdStr]);

  const loadGuardianDocuments = useCallback(() => {
    setGuardianDocumentsLoading(true);
    getGuardianUploadedDocumentsAPI(Number(patientIdStr))
      .then((documents) => {
        setGuardianDocuments(documents ?? []);
      })
      .catch(() => {
        setGuardianDocuments([]);
        toast('Unable to load guardian documents. Please try again.', { type: 'error' });
      })
      .finally(() => {
        setGuardianDocumentsLoading(false);
      });
  }, [patientIdStr]);

  const handleOpenStaffDocuments = () => {
    setStaffDocumentsOpen(true);
    loadStaffDocuments();
  };

  const handleOpenGuardianDocuments = () => {
    setGuardianDocumentsOpen(true);
    loadGuardianDocuments();
  };

  const handleViewPdf = useCallback(
    async (document: PatientUploadedDocument) => {
      setActiveDocument(document);
      setPdfOpen(true);
      setPdfLoading(true);
      revokePdfBlobUrl();

      try {
        const blobUrl = await fetchGeneratedPdfBlobUrlAPI(document.documentId);
        pdfBlobUrlRef.current = blobUrl;
        setPdfBlobUrl(blobUrl);
      } catch (error) {
        console.error('Failed to load PDF:', error);
        toast(error instanceof Error ? error.message : 'Unable to load PDF.', { type: 'error' });
        setPdfOpen(false);
        setActiveDocument(null);
      } finally {
        setPdfLoading(false);
      }
    },
    [revokePdfBlobUrl]
  );

  const handleViewImages = useCallback(async (document: PatientUploadedDocument) => {
    setActiveDocument(document);
    setImagesOpen(true);
    setImagesLoading(true);
    setPreviewImages([]);

    try {
      const images = await fetchRawImagesAPI(document.documentId);
      setPreviewImages(images);
    } catch (error) {
      console.error('Failed to load images:', error);
      toast(error instanceof Error ? error.message : 'Unable to load document images.', { type: 'error' });
      setImagesOpen(false);
      setActiveDocument(null);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  const handleDownloadImages = useCallback(async (document: PatientUploadedDocument) => {
    setDownloadingImagesDocumentId(document.documentId);
    try {
      await downloadRawImagesZipAPI(document.documentId, document.documentName);
    } catch (error) {
      console.error('Failed to download images:', error);
      toast(error instanceof Error ? error.message : 'Unable to download document images.', { type: 'error' });
    } finally {
      setDownloadingImagesDocumentId(null);
    }
  }, []);

  const handleDownloadDocument = async (document: PatientUploadedDocument) => {
    setDownloadingDocumentId(document.documentId);
    try {
      await downloadUploadedDocumentAPI(Number(patientIdStr), document.documentId, document.documentName);
    } catch (error) {
      console.error('Document download failed:', error);
      toast('Download failed. Please try again.', { type: 'error' });
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const handleOpenDeleteConfirm = (document: PatientUploadedDocument) => {
    setDocumentToDelete(document);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (isDeleting) {
      return;
    }
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) {
      return;
    }

    const documentId = documentToDelete.documentId;
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
    setIsDeleting(true);

    try {
      await softDeleteDocumentAPI(documentId);
      setStaffDocuments((prev) => prev.filter((doc) => doc.documentId !== documentId));
      setGuardianDocuments((prev) => prev.filter((doc) => doc.documentId !== documentId));
      toast('Document deleted successfully.', { type: 'success' });
    } catch (error) {
      console.error('Document delete failed:', error);
      toast(error instanceof Error ? error.message : 'Unable to delete the document. Please try again.', {
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePdfClose = () => {
    revokePdfBlobUrl();
    setPdfOpen(false);
    setActiveDocument(null);
  };

  const handleImagesClose = () => {
    setImagesOpen(false);
    setPreviewImages([]);
    setActiveDocument(null);
  };

  const renderDocumentsTable = (
    documents: PatientUploadedDocument[],
    loading: boolean,
    emptyMessage: string,
    showDeleteAction: boolean
  ) => {
    if (loading) {
      return (
        <Stack alignItems='center' sx={{ py: 3 }}>
          <CircularProgress size={28} />
        </Stack>
      );
    }

    if (documents.length === 0) {
      return <Alert severity='info'>{emptyMessage}</Alert>;
    }

    return (
      <TableContainer component={Paper} variant='outlined'>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Document Name</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell>Documents</TableCell>
              {showDeleteAction && <TableCell align='center'>Action</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((document) => {
              const hasPdf = document.sourceType === 'PDF_UPLOAD';
              const hasImages = document.sourceType === 'CAMERA_CAPTURE';

              return (
                <TableRow key={document.documentId}>
                  <TableCell>{document.documentName}</TableCell>
                  <TableCell>{formatDateTime(document.uploadedDate)}</TableCell>
                  <TableCell align='center'>
                    <DocumentActionIcons
                      hasPdf={hasPdf}
                      hasImages={hasImages}
                      documentId={document.documentId}
                      downloadingImagesDocumentId={downloadingImagesDocumentId}
                      downloadingFileDocumentId={downloadingDocumentId}
                      onViewPdf={() => handleViewPdf(document)}
                      onViewImages={() => handleViewImages(document)}
                      onDownloadImages={() => handleDownloadImages(document)}
                      onDownloadFile={hasPdf ? () => handleDownloadDocument(document) : undefined}
                    />
                  </TableCell>
                  {showDeleteAction && (
                    <TableCell align='center'>
                      <Tooltip title='Delete'>
                        <IconButton
                          size='small'
                          aria-label={`Delete ${document.documentName}`}
                          onClick={() => handleOpenDeleteConfirm(document)}
                          disabled={isDeleting}
                        >
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <Typography variant='h2' sx={{ fontSize: 20, fontWeight: 500, lineHeight: 1.4 }} mb={2}>
        Documents
      </Typography>

      <Stack >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputLabel htmlFor='document-name' required>
              Document Name
            </InputLabel>
            <TextField
              id='document-name'
              variant='outlined'
              fullWidth
              value={documentName}
              disabled={isUploading}
              onChange={(event) => setDocumentName(event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor='document-description'>Document Description (Optional)</InputLabel>
            <TextField
              id='document-description'
              variant='outlined'
              fullWidth
              minRows={2}
              value={documentDescription}
              disabled={isUploading}
              onChange={(event) => setDocumentDescription(event.target.value)}
            />
          </Grid>
        </Grid>

        <input ref={inputRef} type='file' accept={STAFF_UPLOAD_ACCEPT_ATTR} hidden onChange={handleFileChange} />
      </Stack>
      <Stack spacing={2} mt={2}>
        <Stack direction='row' alignItems='center' spacing={2} justifyContent='center'>
          
          <Button
            variant='outlined'
            startIcon={<CloudUploadIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={!patientIdStr || isUploading}
          >
            Choose File
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant='contained'
              disabled={isUploading || !selectedFile || !documentName.trim() || !patientIdStr}
              onClick={handleSubmit}
            >
              {isUploading ? <CircularProgress size={22} color='inherit' /> : 'Upload Document'}
            </Button>
          </Box>
        </Stack>

        {selectedFile && (
          <Typography variant='body2' color='text.secondary'>
            Selected File: <strong>{selectedFile.name}</strong>
          </Typography>
        )}

        {fileError && <Alert severity='error'>{fileError}</Alert>}
        <Stack alignItems='center'>
        <Grid alignItems='center' spacing={0}>
          <Button
            sx={{ fontSize: 14 }}
            variant='text'
            onClick={handleOpenStaffDocuments}
          >
            Documents Uploaded by Provider
          </Button>
          <Button
            sx={{ fontSize: 14 }}
            variant='text'
            onClick={handleOpenGuardianDocuments}
          >
            Documents Uploaded by Guardian
          </Button>
        </Grid>
        </Stack>
      </Stack>

      <StyledDialog open={staffDocumentsOpen} onClose={() => setStaffDocumentsOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Uploaded Documents</DialogTitle>
        <DialogContent>
          {renderDocumentsTable(staffDocuments, staffDocumentsLoading, 'No staff uploaded documents found.', true)}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant='outlined' onClick={() => setStaffDocumentsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </StyledDialog>

      <StyledDialog
        open={guardianDocumentsOpen}
        onClose={() => setGuardianDocumentsOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Guardian Uploaded Documents</DialogTitle>
        <DialogContent>
          {renderDocumentsTable(
            guardianDocuments,
            guardianDocumentsLoading,
            'No guardian uploaded documents found.',
            false
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant='outlined' onClick={() => setGuardianDocumentsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </StyledDialog>

      <StyledDialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Document</DialogTitle>
        <DialogContent sx={{ flex: '0 0 auto' }}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: '100%',
              textAlign: 'center',
              py: 1,
            }}
          >
            <Typography variant='body1' sx={{ fontSize: '18px' }} color='text.primary'>
              Are you sure you want to delete this document?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Button type='button' variant='outlined' onClick={handleCloseDeleteConfirm}>
            No
          </Button>
          <Button type='button' variant='contained' color='error' onClick={handleConfirmDelete}>
            Yes
          </Button>
        </DialogActions>
      </StyledDialog>

      <DocumentPreviewDialog
        open={pdfOpen}
        pdfUrl={pdfBlobUrl}
        documentName={activeDocument?.documentName}
        loading={pdfLoading}
        onClose={handlePdfClose}
      />

      <ImagePreviewDialog
        open={imagesOpen}
        images={previewImages}
        documentName={activeDocument?.documentName}
        loading={imagesLoading}
        onClose={handleImagesClose}
      />
    </>
  );
};

export default StaffUploadDocuments;
